import React, { useEffect, useState, useMemo } from "react";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import {
  selectIsTopicFinished,
  selectModulesWithTopics,
} from "../../store/selectors";
import { useParams } from "react-router";
import FooterButtons from "./FooterButtons";
import {
  setKeyTerm,
  toggleBadgeModal,
  toggleUpdateSidebar,
  updateCurrentTopic,
} from "../../store/actions";
import {
  updateCurrentPositionThunk,
  userCompletesSectionThunk,
  userFinishedSubTopicThunk,
  userFinishedTopicThunk,
} from "../../store/thunks";
import { logEvent, logInteractionEvent } from "../../utils/appInsight";
import { useTranslation } from "react-i18next";
import {
  isAllSubTopicsCompletedExceptThirdOne,
  isAllSubTopicsCompleted,
  isModuleFinished,
  isSectionFinished,
} from "../../utils/helpers";
import ModulePageLayout from "../common/ModulePageLayout";
import NotFound from "../pages/NotFound";
import { trackAdobeClick } from "../../utils/adobeLogging";
import PopupNotification from "../popupnotification/PopupNotification";

const ModulePage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const [selectedModule, setSelectedModule] = useState({
    data: {},
    topics: [],
  });
  const [selectedTopic, setSelectedTopic] = useState({});
  const [part, setPart] = useState(0);
  const [notFound, setNotFound] = useState(false);
  const pmApproach = useSelector((state) => state.progress.section);

  const { moduleId, topicId, partIndex } = useParams();
  const modules = useSelector(selectModulesWithTopics);
  const finishedTopics = useSelector((state) => state.progress.finishedTopics);
  const finishedSubTopics = useSelector(
    (state) => state.progress.finishedSubTopics
  );
  const allTopics = useSelector((state) => Object.values(state.topics));

  const parts =
    useSelector((state) =>
      selectedTopic.contentKey ? state.parts[selectedTopic.contentKey] : []
    ) || [];
  const assessmentDone = useSelector((state) => state.progress.assessmentDone);

  const showContinueToActivityLink = useMemo(() => {
    const hasActivity =
      selectedTopic.data && selectedTopic.data.activity.hasActivity;
    if (!hasActivity) return false;

    if (selectedTopic.data.activity.activityKey === "assessment") {
      return !assessmentDone;
    }

    return true;
  }, [selectedTopic, assessmentDone]);

  const nextModuleLink = useMemo(() => {
    if (selectedModule && selectedModule.topics) {
      const topicIndex = _.findIndex(selectedModule.topics, {
        contentKey: selectedTopic.contentKey,
      });
      if (topicIndex + 1 < selectedModule.topics.length) {
        return `${selectedModule.contentKey}/${
          selectedModule.topics[topicIndex + 1].contentKey
        }`;
      }

      const index = _.findIndex(modules, {
        contentKey: selectedModule.contentKey,
      });
      if (index + 1 < modules.length) {
        return `${modules[index + 1].contentKey}`;
      }
    }

    return null;
  }, [modules, selectedModule, selectedTopic]);

  useEffect(() => {
    dispatch(updateCurrentPositionThunk(location.pathname));
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    partIndex
      ? setPart(parseInt(partIndex, 10))
      : setPart(!moduleId || moduleId.includes("introduction-module") ? 0 : 1);
    if (modules.length > 0) {
      if (!moduleId) {
        setSelectedModule(modules[0]);
        setSelectedTopic(modules[0].topics[0]);
        setNotFound(false);
      }

      if (moduleId && !topicId) {
        const module = _.find(modules, { contentKey: moduleId });
        if (module) {
          setSelectedModule(module);
          setSelectedTopic(module.topics[0]);
          setNotFound(false);
        } else {
          setNotFound(true);
        }
      }

      if (moduleId && topicId) {
        const module = _.find(modules, { contentKey: moduleId });
        if (module) {
          setSelectedModule(module);
          const t = _.find(module.topics, { contentKey: topicId });
          if (t) {
            setSelectedTopic(t);
            setNotFound(false);
          } else {
            setNotFound(true);
          }
        } else {
          setNotFound(true);
        }
      }
    }
  }, [moduleId, topicId, partIndex, modules]);

  useEffect(() => {
    if (selectedTopic && selectedTopic.contentKey) {
      logEvent({
        name: "TopicOpened",
        properties: {
          section: pmApproach,
          moduleKey: selectedTopic.parentContentId,
          topicKey: selectedTopic.contentKey,
        },
      });
      logInteractionEvent("TopicOpened", {
        section: pmApproach,
        topicKey: selectedTopic.contentKey,
      });
      if (selectedTopic.parentContentId.includes("introduction-module")) {
        dispatch(toggleUpdateSidebar());
      }
      dispatch(updateCurrentTopic(selectedTopic.contentKey));
    }
  }, [selectedTopic, dispatch]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [moduleId, topicId, partIndex]);

  const isFinished = useSelector((state) =>
    selectIsTopicFinished(state, selectedTopic.contentKey)
  );
  const hasActivity = useMemo(() => {
    return (
      selectedTopic &&
      selectedTopic.data &&
      selectedTopic.data.activity.hasActivity
    );
  }, [selectedTopic]);

  useEffect(() => {
    const contentKey = `${
      topicId === undefined ? selectedTopic.contentKey : topicId
    }-${partIndex ? partIndex : 1}`;
    dispatch(
      userFinishedSubTopicThunk([...finishedSubTopics, contentKey])
    ).then((response) => {
      if (
        part === 3 &&
        !isFinished &&
        !hasActivity &&
        isAllSubTopicsCompleted(response, parts, selectedTopic)
      ) {
        dispatch(
          userFinishedTopicThunk([...finishedTopics, selectedTopic.contentKey])
        ).then((finishedTopics) => {
          logEvent({
            name: "LessonCompleted",
            properties: {
              section: pmApproach,
              moduleKey: selectedTopic.parentContentId,
              topicKey: selectedTopic.contentKey,
            },
          });
          logInteractionEvent("LessonCompleted", {
            section: pmApproach,
            moduleKey: selectedTopic.parentContentId,
            topicKey: selectedTopic.contentKey,
          });
          if (isModuleFinished(finishedTopics, moduleId, allTopics)) {
            logEvent({
              name: "ModuleCompleted",
              properties: {
                section: pmApproach,
                moduleKey: moduleId,
              },
            });
            logInteractionEvent("ModuleCompleted", {
              section: pmApproach,
              moduleKey: moduleId,
            });

            if (isSectionFinished(finishedTopics, allTopics, pmApproach)) {
              dispatch(userCompletesSectionThunk(pmApproach));
            }
          }
        });
      }
    });
  }, [moduleId, topicId, partIndex, part]);

  const showKeyTerm = (e) => {
    e.preventDefault();
    dispatch(setKeyTerm(e.target.dataset.term));
  };

  useEffect(() => {
    document
      .querySelectorAll("a.term-link")
      .forEach((el) => el.addEventListener("click", showKeyTerm));

    return () => {
      document
        .querySelectorAll("a.term-link")
        .forEach((el) => el.removeEventListener("click", showKeyTerm));
    };
  });

  const getHtml = (part) => {
    return part.fullContent;
  };

  if (notFound) return <NotFound />;

  return (
    <>
      {part === 3 &&
        !hasActivity &&
        !isSectionFinished(finishedTopics,allTopics,pmApproach) &&
        !isAllSubTopicsCompletedExceptThirdOne(
          finishedSubTopics,
          parts,
          selectedTopic
        ) && <PopupNotification />}
      <div adoberegion={`${pmApproach}-module-page`}>
        <ModulePageLayout
          module={selectedModule}
          topic={selectedTopic}
          part={part}
        >
          <div>
            {parts.length > 0 && (
              <div dangerouslySetInnerHTML={{ __html: getHtml(parts[part]) }} />
            )}
            {part === 3 && (
              <FooterButtons
                showActivityButton={showContinueToActivityLink}
                topicData={selectedTopic.data}
                color={selectedModule.data.accentColor}
                downloadLabel={t("downloadTemplate")}
              />
            )}
          </div>

          <footer className="flex justify-between mx-auto w-full pb-12">
            {part > 1 ? (
              <Link
                id="back"
                onClick={() => {
                  trackAdobeClick(null, {
                    linkTitle: t("back"),
                    linkModule: `${pmApproach}-module-page`,
                    targetURL: `/app/${selectedModule.contentKey}/${
                      selectedTopic.contentKey
                    }${part > 1 ? "/" + (part - 1) : ""}`,
                  });
                }}
                to={`/app/${selectedModule.contentKey}/${
                  selectedTopic.contentKey
                }${part > 1 ? "/" + (part - 1) : ""}`}
                className={`text-${selectedModule.data.accentColor} previous font-secondary uppercase font-bold relative pl-30 pt-4 focus:outline-none`}
              >
                <span className="icon-prev absolute v-middle l-0 text-22" />{" "}
                {t("back")}
              </Link>
            ) : (
              <div>&nbsp;</div>
            )}

            {part < 3 && (
              <Link
                id="continue"
                onClick={() => {
                  trackAdobeClick(null, {
                    linkTitle: t("continue"),
                    linkModule: `${pmApproach}-module-page`,
                    targetURL: `/app/${selectedModule.contentKey}/${
                      selectedTopic.contentKey
                    }/${part + 1}`,
                  });
                }}
                to={`/app/${selectedModule.contentKey}/${
                  selectedTopic.contentKey
                }/${part + 1}`}
                className={`text-${selectedModule.data.accentColor} next font-secondary uppercase font-bold relative pr-30 pt-4 focus:outline-none`}
              >
                <span className="icon-next absolute v-middle r-0 text-22" />
                {t("continue")}
              </Link>
            )}

            {part === 3 && showContinueToActivityLink && (
              <Link
                id="continue"
                onClick={() => {
                  trackAdobeClick(null, {
                    linkTitle: t("continue"),
                    linkModule: `${pmApproach}-module-page`,
                    targetURL: `/app/activity/${selectedTopic.data.activity.activityKey}`,
                  });
                }}
                to={`/app/activity/${selectedTopic.data.activity.activityKey}`}
                className={`text-${selectedModule.data.accentColor} next font-secondary uppercase font-bold relative pr-30 pt-4 focus:outline-none`}
              >
                <span className="icon-next absolute v-middle r-0 text-22 " />
                {t("continue")}
              </Link>
            )}

            {part === 3 && !showContinueToActivityLink && nextModuleLink && (
              <Link
                id="continue"
                onClick={() => {
                  trackAdobeClick(null, {
                    linkTitle: t("nextLesson"),
                    linkModule: `${pmApproach}-module-page`,
                    targetURL: `/app/${nextModuleLink}`,
                  });
                }}
                to={`/app/${nextModuleLink}`}
                className={`text-${selectedModule.data.accentColor} next font-secondary uppercase font-bold relative pr-30 pt-4 focus:outline-none`}
              >
                <span className="icon-next absolute v-middle r-0 text-22" />
                {t("nextLesson")}
              </Link>
            )}

            {part === 3 && !showContinueToActivityLink && !nextModuleLink && (
              <Link
                to={location?.pathname}
                onClick={(e) => {
                  dispatch(toggleBadgeModal());
                  trackAdobeClick(null, {
                    linkTitle: t("Download Badge"),
                    linkModule: `${pmApproach}-module-page`,
                    targetURL: "/app/pages/learn-more",
                  });
                }}
                id="continue"
                className={`text-${selectedModule.data.accentColor} next font-secondary uppercase font-bold relative pr-30 pt-4 focus:outline-none`}
              >
                <span className="icon-next absolute v-middle r-0 text-22" />
                {t("Download Badge")}
              </Link>
            )}
          </footer>
        </ModulePageLayout>
      </div>
    </>
  );
};

export default ModulePage;
